import * as Sentry from '@sentry/react';
import { getLangs, getLangScopes } from 'app/crud/langs.crud';
import { parseLanguages } from 'app/utils/lang';
import { compareField } from 'app/utils/utils';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, select, takeLatest } from 'redux-saga/effects';
import { PERSIST_LANGS, REDUCER_LANGS } from '../conf';

export const actionTypes = {
  GetLangs: 'cms/GET_LANGS',
  SetLangs: 'cms/SET_LANGS',
  SetLangsError: 'cms/SET_LANGS_ERROR',
  GetLangScopes: 'cms/GET_LANG_SCOPES',
  SetLangScopes: 'cms/SET_LANG_SCOPES',
};

const initialState = {
  langs: [],
  scopes: [],
  loading: false,
  error: null,
};

export const reducer = persistReducer({ storage, key: PERSIST_LANGS, whitelist: ['langs'] }, (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.GetLangs:
      return { ...state, loading: true, error: null };

    case actionTypes.SetLangs: {
      const { response, isFilter } = action.payload;
      if (response?.status === 'success' && response.data) {
        const languages = parseLanguages(response.data, isFilter);
        languages.sort((a, b) => {
          return compareField(a, b, 'description');
        });
        return { ...state, langs: languages, loading: false, error: null };
      }
      return { ...state, loading: false };
    }

    case actionTypes.SetLangsError: {
      return { ...state, loading: false, error: action.payload };
    }

    case actionTypes.SetLangScopes: {
      const { data } = action.payload;
      return { ...state, scopes: data };
    }

    default:
      return state;
  }
});

export const selectors = {
  getLangs: (state) => {
    return state.entities?.[REDUCER_LANGS] ? state.entities[REDUCER_LANGS].langs : null;
  },
  getLangScopes: (state) => {
    return state.entities?.[REDUCER_LANGS] ? state.entities[REDUCER_LANGS].scopes : null;
  },
};

export const actions = {
  getLangs: (params) => ({ type: actionTypes.GetLangs, payload: { ...params } }),
  fulfillLangs: (response, isFilter) => ({ type: actionTypes.SetLangs, payload: { response, isFilter } }),
  setLangsError: (message) => ({ type: actionTypes.SetLangsError, payload: message }),
  getLangScopes: (params) => ({ type: actionTypes.GetLangScopes, payload: params }),
  fulfillLangScopes: (response) => ({ type: actionTypes.SetLangScopes, payload: response }),
};

function* fetchLangs() {
  try {
    const { data } = yield getLangs();
    if (!data) throw new Error('Empty response from getLangs');
    yield put(actions.fulfillLangs(data));
  } catch (error) {
    Sentry.captureException(error, { tags: { saga: 'fetchLangs' } });
    yield put(actions.setLangsError(error?.message || 'Unknown error'));
  }
}

function* fetchLangScopes() {
  const { data } = yield getLangScopes();
  yield put(actions.fulfillLangScopes(data));
}

function* getLangsIfNeeded({ payload }) {
  const langs = yield select(selectors.getLangs);
  if (!langs || langs.length === 0 || payload?.isRefresh) {
    yield fetchLangs();
  }
}

function* getLangScopesIfNeeded() {
  const scopes = yield select(selectors.getLangScopes);
  if (!scopes || scopes.length === 0) {
    yield fetchLangScopes();
  }
}

export function* saga() {
  yield takeLatest(actionTypes.GetLangs, getLangsIfNeeded);
  yield takeLatest(actionTypes.GetLangScopes, getLangScopesIfNeeded);
}
